import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import { CreateBusinessSchema, UpdateBusinessSchema } from "./business.schema";

export const BusinessIdInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

export const CreateBusinessInputSchema = CreateBusinessSchema;

export const UpdateBusinessInputSchema = z.object({
  businessId: z.string().trim().min(1),
  updates: UpdateBusinessSchema,
});

export const AddBusinessClassInputSchema = z.object({
  businessId: z.string().trim().min(1),
  name: z.string().trim().min(1, "Class name is required").max(100),
});

export const DeleteBusinessClassInputSchema = z.object({
  classId: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const listBusinesses = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    return withSentrySpan("business.list", async () => {
      const {
        listBusinessesForUser,
        resolveActiveBusinessId,
        getUserActiveBusinessId,
      } = await import("./business.server");
      const [businesses, savedActiveId] = await Promise.all([
        listBusinessesForUser(context.user.id),
        getUserActiveBusinessId(context.user.id),
      ]);
      const activeBusinessId = resolveActiveBusinessId(
        businesses,
        savedActiveId,
      );
      return { businesses, activeBusinessId };
    });
  });

export const getBusinessDetail = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BusinessIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.getDetail", async () => {
      const { getBusinessDetailForUser } = await import("./business.server");
      return getBusinessDetailForUser(context.user.id, data.businessId);
    });
  });

export const setActiveBusiness = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BusinessIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.setActive", async () => {
      const { getBusinessForUser, updateUserActiveBusiness } =
        await import("./business.server");
      const owned = await getBusinessForUser(context.user.id, data.businessId);
      if (!owned)
        throw Response.json({ error: "Business not found" }, { status: 404 });
      await updateUserActiveBusiness(context.user.id, owned.id);
      return { activeBusinessId: owned.id };
    });
  });

export const createBusiness = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateBusinessInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.create", async () => {
      const { createBusinessForUser } = await import("./business.server");
      return createBusinessForUser(context.user.id, data);
    });
  });

export const updateBusiness = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateBusinessInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.update", async () => {
      const { updateBusinessForUser } = await import("./business.server");
      const result = await updateBusinessForUser(
        context.user.id,
        data.businessId,
        data.updates,
      );
      if (!result)
        throw Response.json({ error: "Business not found" }, { status: 404 });
      return result;
    });
  });

export const deleteBusiness = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BusinessIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.delete", async () => {
      const { deleteBusinessForUser } = await import("./business.server");
      const result = await deleteBusinessForUser(
        context.user.id,
        data.businessId,
      );
      if (!result)
        throw Response.json({ error: "Business not found" }, { status: 404 });
      return result;
    });
  });

export const addBusinessClass = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => AddBusinessClassInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.addClass", async () => {
      const { addBusinessClassForUser } = await import("./business.server");
      const result = await addBusinessClassForUser(
        context.user.id,
        data.businessId,
        data.name,
      );
      if (!result)
        throw Response.json({ error: "Business not found" }, { status: 404 });
      return result;
    });
  });

export const deleteBusinessClass = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DeleteBusinessClassInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("business.deleteClass", async () => {
      const { deleteBusinessClassForUser } = await import("./business.server");
      const result = await deleteBusinessClassForUser(
        context.user.id,
        data.businessId,
        data.classId,
      );
      if (!result)
        throw Response.json({ error: "Business not found" }, { status: 404 });
      return result;
    });
  });
